import React, { useEffect, useState } from "react";
import {
  useLazyGetAcademyHomeDataQuery,
  useLazyGetCourseCertificateQuery,
} from "../../store/api/academyApi";
import { Link } from "react-router-dom";
import { setDropdownList, setPage } from "../../store/slices/academySlice";
import { useDispatch } from "react-redux";
import { DropdownList } from "./academy-home";
import LockedOverlay from "../common/locked_content";
import { openModal } from "../../store/slices/modalSlice";
import { MODAL_NAME } from "../../constants";
import BecomeMember from "../common/membership";
import { useAppSelector } from "../../hooks/store-hooks";
import {
  AcademyCertificateIcon,
  AcademyComicsCloseButton,
  DownloadIcon,
} from "../../icons/academy-icons";
import Modal from "../common/modals";
import Button from "../common/button";
import { CopyLink, Linkedin } from "../../icons";
import { formatLearnersCount } from "../../helpers";
import moment from "moment";
import { ClockIcon } from "../../icons/tags-icons";
import { trackEvent } from "../../analytics";

export interface Course {
  course_id: string;
  title: string;
  thumbnail: string;
  lessons_count: number;
  content_duration: number;
  is_subscribed?: boolean;
  footer: {
    course_status: string;
    learners: {
      count: number;
      profile_pics: string[];
    };
    progress: {
      completion_percentage: number;
    };
  };
  expires_at?: Date;
}

export const CourseStatus: { [key: string]: string } = {
  START: "Start",
  RESUME: "Resume",
  COMPLETED: "Completed",
  UNLOCK: "Unlock",
};

function CoursesComponent() {
  const [triggerGetAcademyAllTabData] = useLazyGetAcademyHomeDataQuery();
  const [triggerGetCourseCertificate] = useLazyGetCourseCertificateQuery();
  const [courseData, setCourseData] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [certificate, setCertificate] = useState<any>("");
  const dispatch = useDispatch();
  const { modalName } = useAppSelector((state) => state.modal);

  const fetchCourseData = async () => {
    const resp = await triggerGetAcademyAllTabData({
      type: "course",
    }).unwrap();
    if (resp?.success) {
      dispatch(setPage("courses"));
      const dropdownList = [...DropdownList];
      const dropdown_list = resp.data?.courses_list?.map((course: Course) => ({
        title: course.title,
        id: course.course_id,
      }));
      dropdownList[1] = { ...dropdownList[1], dropdown: dropdown_list };
      dispatch(setDropdownList(dropdownList));
      setCourseData(resp.data);
    }
  };

  const getCourseCertificate = async (courseId: string) => {
    try {
      const resp = await triggerGetCourseCertificate({
        courseId,
      }).unwrap();
      if (resp?.success) {
        setIsModalOpen(true);
        setCertificate(resp.data);
        trackEvent("course_certificate_opened", {
          course_id: courseId,
          course_title: resp.data?.course_name,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchCourseData();
  }, []);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = certificate.certificate_url;
    link.download = certificate.certificate_url.split("/").pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(certificate.certificate_url);
      setCopySuccess(true);
    } catch (err) {
      setCopySuccess(false);
      console.log(err);
    }
  };

  const handleLinkedInRedirect = () => {
    const date = new Date(certificate.completion_date);
    const issueYear = date.getFullYear();
    const issueMonth = date.getMonth() + 1;
    const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(certificate.course_name)}&organizationId=${certificate.issuing_organization}&issueYear=${issueYear}&issueMonth=${issueMonth}&&certId=${encodeURIComponent(certificate.credential_id)}&certUrl=${encodeURIComponent(certificate.certificate_url)}`;
    window.location.href = linkedInUrl;
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="w-full mx-auto p-8 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Courses</h2>
        <div className="-mx-3 grid xl:grid-cols-3 place-items-center sm:grid-cols-2 grid-cols-1">
          {courseData?.courses_list?.map((course: Course, index: number) => (
            <div
              key={index}
              className="max-w-[360px] min-w-[300px] w-full p-3"
              onClick={() => {
                trackEvent("course_module_list_opened", {
                  course_id: course?.course_id,
                  course_title: course?.title,
                });
              }}
            >
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="relative">
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full object-cover"
                  />
                  {course?.is_subscribed ? (
                    <div className="absolute inset-0 flex flex-col justify-between transition-opacity duration-300">
                      <div className="text-white px-4 py-2">
                        <p>
                          {course.lessons_count} Lessons •{" "}
                          {Math.floor(course.content_duration / 60)}h{" "}
                          {course.content_duration % 60}m
                        </p>
                        <h3 className="text-[28px] leading-[32px] font-bold w-3/4">
                          {course.title}
                        </h3>
                      </div>
                      {course.expires_at && (
                        <div
                          className={
                            `text-black text-[14px] mx-4 my-4 py-1 px-2 rounded-full bg-[#ECD996] w-fit flex items-center gap-1` +
                            (moment(course.expires_at).diff(moment(), "days") +
                              1 <
                            30
                              ? " bg-[#EE4D37] text-white"
                              : "")
                          }
                        >
                          <ClockIcon
                            fillColor={
                              moment(course.expires_at).diff(moment(), "days") +
                                1 <
                              30
                                ? "white"
                                : "#090909"
                            }
                          />{" "}
                          <p className="font-bold">
                            {moment(course.expires_at).diff(moment(), "days") +
                              1}{" "}
                            Days{" "}
                          </p>
                          {` `} <p>Left</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <LockedOverlay blur={false} />
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">
                        {course.title}
                      </h3>
                      <div className="flex items-center">
                        <div
                          className={`flex -space-x-4 rtl:space-x-reverse float-left ${
                            course?.footer?.learners?.count === 0 && "hidden"
                          }`}
                        >
                          {/* Profile images */}
                          {course?.footer?.learners?.profile_pics?.map(
                            (profilePic: string, index: number) => (
                              <div
                                key={index}
                                className="w-8 h-8 border-2 border-[#EBEBEB] rounded-full overflow-hidden relative"
                              >
                                <img
                                  className="w-full h-full absolute inset-0 object-cover"
                                  src={profilePic}
                                  alt="Profile"
                                />
                              </div>
                            ),
                          )}
                        </div>
                        <p className="ml-2 text-sm text-gray-800 font-semibold">
                          {formatLearnersCount(
                            course?.footer?.learners?.count || 0,
                          )}
                          + Learners
                        </p>
                      </div>
                    </div>

                    {course.is_subscribed ? (
                      <button className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm hover:bg-blue-600 transition-colors duration-300 font-semibold">
                        {course?.footer?.course_status === "COMPLETED" ? (
                          <div
                            className="flex gap-2 items-center"
                            onClick={() => {
                              getCourseCertificate(course.course_id);
                            }}
                          >
                            <AcademyCertificateIcon />
                            <p>Certificate</p>
                          </div>
                        ) : (
                          <Link to={`/academy/courses/${course.course_id}`}>
                            {CourseStatus[course?.footer?.course_status]}
                          </Link>
                        )}
                      </button>
                    ) : (
                      <button
                        className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm hover:bg-blue-600 transition-colors duration-300"
                        onClick={() => {
                          dispatch(
                            openModal({
                              modalName: MODAL_NAME.BECOME_MEMBER,
                            }),
                          );
                        }}
                      >
                        {CourseStatus[course?.footer?.course_status]}
                      </button>
                    )}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <div className="w-2/3 bg-gray-700 rounded-lg h-3">
                      <div
                        className="bg-gradient-text h-3 rounded-lg"
                        style={{
                          width:
                            `${course?.footer?.progress?.completion_percentage}%` ||
                            0,
                        }}
                      ></div>
                    </div>
                    <p className="text-xs font-semibold">
                      {course?.footer?.progress?.completion_percentage || 0}%
                      Complete
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <Modal
            isOpen={isModalOpen}
            blurBg={true}
            setModalOpen={setIsModalOpen}
            customClass="!bg-transparent md:!w-fit !max-w-fit md:!max-w-fit !max-h-fit !md:h-fit !shadow-none  md:!p-0 !p-0 !overflow-visible"
          >
            <div className=" w-fit !bg-transparent flex">
              <div className=" w-fit h-fit">
                <div className="w-fit h-fit flex flex-col">
                  <div className="bg-white m-auto !w-[578px] h-fit flex flex-col items-center">
                    <h1 className="text-2xl font-semibold my-4">
                      Course Certificate
                    </h1>
                    <div
                      className="w-[90%] h-[368px] bg-gray-800 rounded-lg overflow-hidden"
                      style={{
                        boxShadow: "0px 0px 14.68px 0px #00000026",
                      }}
                    >
                      <iframe
                        src={`${certificate.certificate_url}#toolbar=0`}
                        className="w-full h-full"
                        title="Certificate"
                      ></iframe>
                    </div>
                    <p className="text-2xl font-bold mt-4 mb-2">
                      {certificate.course_name}
                    </p>
                    <p className="">
                      Completed on {certificate?.completion_date}
                    </p>
                    <Button
                      customClasses="w-[90%] py-4 rounded-lg mt-4 text-[17px]"
                      variant="primary"
                      onClick={handleLinkedInRedirect}
                    >
                      <div className="flex items-center gap-2">
                        <Linkedin fillColor="currentColor" width={20} />
                        <p>Add to LinkedIn</p>
                      </div>
                    </Button>
                    <div className="w-[90%] flex gap-2 my-4">
                      <Button
                        customClasses="w-1/2 py-2 rounded-lg"
                        variant="secondary"
                        onClick={handleDownload}
                      >
                        <div className="flex items-center">
                          <DownloadIcon width={20} strokeColor="currentColor" />
                          <p>Download</p>
                        </div>
                      </Button>

                      <Button
                        customClasses="w-1/2 py-4 rounded-lg"
                        variant="secondary"
                        onClick={handleCopy}
                      >
                        <div className="flex items-center">
                          <CopyLink fillColor="currentColor" width={20} />
                          <p>{copySuccess ? "Copied" : "Copy Link"}</p>
                        </div>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="absolute top-6 right-8"
                onClick={() => setIsModalOpen(false)}
              >
                <AcademyComicsCloseButton width={16} color="black" />
              </div>
            </div>
          </Modal>
        </div>
      </div>
      {modalName === MODAL_NAME.BECOME_MEMBER && <BecomeMember />}
    </div>
  );
}

export default CoursesComponent;
